import type { DunningStatus } from '@salonomy/types';
import { Button, Pill } from '@salonomy/ui';
import { pluralize } from '../../lib/format.js';
import { DELINQUENCY_TONE, DUNNING_STAGE_LABEL } from './format.js';

/**
 * The dunning / read-only banner (brief 10 §4; ADR-0011 §3). EVERYTHING here is read from the API's
 * /v1/billing/dunning view — the named stage, how far through the ladder we are, when read-only is
 * reached, and the PUBLISHED retry/grace/read-only schedule behind it (plan config — the same numbers
 * WS3's terms quote). Nothing is invented and nothing is computed client-side: the salon sees exactly
 * when new commerce freezes, never a surprise. The "update card" CTA is the way out.
 *
 * Renders only when the salon is actually delinquent (`delinquencyState !== 'none'`); a healthy account
 * shows no banner at all. Read-only means new bookings are frozen — data stays readable and exportable
 * (billing state NEVER gates emergency data access — brief 10 non-negotiable).
 */
export function DunningBanner({
  dunning,
  onUpdateCard,
}: {
  dunning: DunningStatus;
  onUpdateCard: () => void;
}) {
  if (dunning.delinquencyState === 'none') return null;

  const readOnly = dunning.delinquencyState === 'read_only';
  const { schedule } = dunning;

  return (
    <div className={`billing-banner billing-banner--${dunning.delinquencyState}`}>
      <div className="billing-banner__head">
        <div className="billing-banner__title">
          {readOnly
            ? 'Your account is read-only — payment needed'
            : 'We couldn’t take your last payment'}
        </div>
        <Pill tone={DELINQUENCY_TONE[dunning.delinquencyState]}>
          {DUNNING_STAGE_LABEL[dunning.stage]}
        </Pill>
      </div>

      <div className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
        {readOnly ? (
          <>
            New bookings are paused until a payment goes through. Your diary, clients and reports
            stay fully visible and exportable — we never lock you out of your own data, and nothing
            is ever deleted for non-payment. Add a working card to lift this straight away.
          </>
        ) : (
          <>
            We’ll retry automatically on the schedule below.{' '}
            {dunning.daysUntilReadOnly != null ? (
              <>
                Your account becomes read-only in{' '}
                <strong>{pluralize(dunning.daysUntilReadOnly, 'day')}</strong> if it isn’t settled
                by then.
              </>
            ) : null}{' '}
            You can fix this in one step by updating your card.
          </>
        )}
      </div>

      {/* The PUBLISHED ladder behind the stage — plan config, the exact numbers WS3's terms quote. */}
      <div className="billing-banner__schedule">
        <Pill tone="soft" className="mono">
          Retries on day {schedule.retryDays.length ? schedule.retryDays.join(', ') : '—'}
        </Pill>
        <Pill tone="soft" className="mono">
          {pluralize(schedule.graceDays, 'grace day')}
        </Pill>
        <Pill tone="soft" className="mono">
          Read-only from day {schedule.readonlyDay}
        </Pill>
        {dunning.daysElapsed != null ? (
          <Pill tone="neutral" className="mono">
            Day {dunning.daysElapsed} of this episode
          </Pill>
        ) : null}
      </div>

      <div>
        <Button size="sm" variant={readOnly ? 'danger' : 'primary'} onClick={onUpdateCard}>
          Update card
        </Button>
      </div>
    </div>
  );
}
